import {
  DataAction,
  Payload,
  StateRepository
} from '@angular-ru/ngxs/decorators'
import { Selector, State, Store } from '@ngxs/store'
import { inject, Injectable } from '@angular/core'
import {
  BehaviorSubject,
  catchError,
  distinct,
  EMPTY,
  filter,
  finalize,
  interval,
  map,
  merge,
  Observable,
  of,
  skipWhile,
  Subscription,
  switchMap,
  take,
  takeUntil,
  tap,
  throttleTime,
  timer
} from 'rxjs'
import {
  PatientInterface,
  PatientVitalsTakenGroup
} from '../../shared/model/patient'
import { MeasurementState } from '../measurement/measurement.state'
import moment from 'moment'
import { PatientState } from '../patient/patient.state'
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories'
import { cloneDeep, isNull, orderBy, round, uniq } from 'lodash-es'
import {
  CheckShiftSentEmrInformationInterface,
  ManualVitalsInterface,
  ModeType,
  PatientExportedVitalsWithTimestamp,
  PatientVItalInsightsObservationFields,
  PatientVItalObservationFields,
  PatientVitalsInterface,
  PatientVitalsStatus,
  ReportInterface,
  ReportMedicationInterface,
  ReportMode,
  ReportsVitalsInterface,
  ReportType,
  ReportVitalsFields,
  SentEmrInformation,
  VitalsFields,
  VitalStatusIcon,
  VitalStatusIndicator
} from '../../shared/model/report.model'
import { BackendService } from '../../shared/services/backend.service'
import { PccState } from '../pcc/pcc.state'
import { DeviceState } from '../device/device.state'
import { TreatmentPlanDTO } from '../../shared/model/treatment-plan'
import {
  ExportAllInterface,
  MethodsDataSelectedKeys
} from '../../shared/model/export'
import { ExportState } from '../export/export.state'
import { PatientExportDTO } from '../../shared/model/pcc.model'
import { NotificationService } from '../../shared/services/notification.service'
import { DepartmentState } from '../department/department.state'
import { entitiesFilter } from '../../core/helpers/filter'
import {
  DeviceCriticalStatus,
  DeviceDTO,
  DeviceInterface,
  DeviceModel
} from '../../shared/model/device.model'
import { StoreEventsService } from '../store-events.service'
import { checkEmrRules } from '../../core/helpers/check-emr-rules'
import {
  ObservationField,
  ObservationFields,
  ObservationLatest,
  PatientObservationDTO
} from '../../shared/model/patient-observation'
import { RootStore } from '../root-store'
import { ShiftPlanerState } from '../shift-planer/shift-planer.state'
import { Shift, ShiftType } from '../../shared/model/shift-planer.model'
import { combineTreatmentPlans } from '../../core/helpers/combine-treatment-plans'
import { checkVitalsToTreatmentPlan } from '../../core/helpers/check-vitals-to-treatment-plan'
import { WebSocketService } from '../../shared/services/web-socket.service'
import { AuthState } from '../auth/auth.state'
import {
  forceReadStatus,
  NewReadSocketModel
} from '../../shared/model/new-read-socket-model'
import { environment } from '../../environments/environment'
import { WarningTitle } from '../../shared/constants/warning.constant'
import { isTakingTodayVitalsRequired } from '../../core/helpers/is-taking-vitals-today-required/is-taking-vitals-today-required'
import { UserState } from '../user/user.state'
import { isFromCurrentShift } from '../../core/helpers/is-in-shift-range'
import { DepartmentDTO } from '../../shared/model/permission.model'
import { PreferenceState } from '../preference/preference.state'
import { checkVitalToEmrRules } from '../../core/helpers/check-vital-emr-rules'
import { checkFreshVitalCurrentShift } from '../../core/helpers/check-fresh-vital-current-shift'
import { checkIsManual } from '../../core/helpers/check-is-manual'
import { InsightDTO } from '../../shared/model/insight.model'
import { extractSpecificInsightBySubject } from '../../core/helpers/extract-specific-insight-by-subject'
import { AlertSubject } from '../../shared/model/alert'
import { abnormalMissingFilter } from '../../core/helpers/abnormal-missing-filter'
import { CheckMissingVital } from '../../core/helpers/check-missing-vital'
import { bodyTemperatureSetting } from '../../core/helpers/body-temperature/body-temperature-setting'
import { isAnyVitalsMissing } from '../../core/helpers/is-any-shift-vitals-missing/is-any-shift-vitals-missing'
import { requiredVitals } from '../../core/helpers/required-measurements/required-measurements'
import { checkCriticalRules } from '../../core/helpers/check-critical-rules'
import { EMRConfig } from '../../shared/model/emr-config'
import { OrganizationState } from '../organization/organization.state'
import { LogrocketTrackKeys } from '../../shared/enums/logrocket-track-keys'
import { getVitalsFromReportingObject } from '../../core/helpers/get-vitals-from-reporting-object'
import { NgxPermissionsService } from 'ngx-permissions'
import { Permission } from '../../core/enums/permission.enum'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { BffPatient } from '../../shared/model/bff/patient'
import { BffObservationExport } from '../../shared/model/bff/observation-export'
import { LogrocketPFFacade } from '../../shared/services/logrocket-pf-facade.service'
import { convertMeasurementFromStringPayload } from '../../core/helpers/convert-measurement-from-string-payload'
import { PermissionsService } from '../../shared/services/permissions.service'
import { ObservationExportMethodsData } from '../../shared/types/bff-observations-methods-response'
import { PerformanceState } from '../performance/performance.state'

export const reportFeatureName = 'report'

@StateRepository()
@State<ReportInterface>({
  name: reportFeatureName,
  defaults: {
    requestPatientsVitals: [],
    monitorPatientIds: [],
    forceReadDevicesStatus: [],
    textFilter: '',
    isAbnormalMissingFilter: false,
    type: ReportType.CurrentShift,
    mode: ModeType.Latest,
    loading: false,
    isMeasurementSending: false,
    isMeasurementSendingFailed: false,
    isForceReadProcess: false,
    currentShift: null,
    medication: [],
    hasUnsentVitalsPopupShown: true,
    isModalWithBulkSendingPatientsVisible: false,
    isMessageAboutShortCutShown: false,
    successfullySavedMeasurementsOrReportedPatientsIds: [],
    isPatientVitalsTakenPopupVisible: false,
    isPatientVitalsWereTakenWithoutSelectionPopupVisible: {
      closedTime: null,
      isOpened: false
    },
    isWarningAboutNotSavedOrSentMeasurementsVisible: false,
    nowDate: new Date().toISOString()
  }
})
@Injectable()
export class ReportState extends NgxsDataRepository<ReportInterface> {
  private readonly patientState = inject(PatientState)
  private readonly exportState = inject(ExportState)
  private readonly deviceState = inject(DeviceState)
  private readonly measurementState = inject(MeasurementState)
  private readonly backendService = inject(BackendService)
  private readonly ntfService = inject(NotificationService)
  private readonly pccState = inject(PccState)
  private readonly departmentState = inject(DepartmentState)
  private readonly storeEvents = inject(StoreEventsService)
  private readonly store = inject(Store)
  private readonly webSocketService = inject(WebSocketService)
  private readonly authState = inject(AuthState)
  private readonly shiftPlannerState = inject(ShiftPlanerState)
  private readonly preferenceState = inject(PreferenceState)
  private readonly ngxPermissionsService = inject(NgxPermissionsService)
  private readonly userState = inject(UserState)
  private readonly performanceState = inject(PerformanceState)
  private readonly logrocketPFFacade = inject(LogrocketPFFacade)
  private readonly permissionsService = inject(PermissionsService)

  reportsVitalsData: ReportsVitalsInterface = {
    patientActions: []
  }
  updatePatientsIds: string[] = []
  subscriptionNewReadSocket$: Subscription
  timeout: any
  lastPatientReportTime: any = {}
  isUserRN$: Observable<boolean> = inject(Store).select(UserState.isUserRN)
  showed30MinutesMessage = false
  showed10MinutesMessage = false
  private reportStateSubscription: Subscription
  private checkTimeSubscription: Subscription
  private medicationSubscription: Subscription
  private readonlylocalDeviceMeasurementsMessageSubscription: Subscription
  private successfullySentCurrentShiftPatientAllMeasurements$ =
    new BehaviorSubject<Partial<PatientVitalsInterface> | null>(null)
  public readonly successfullySentCurrentShiftPatientAllMeasurementsObs$ =
    this.successfullySentCurrentShiftPatientAllMeasurements$.asObservable()
  private currentShiftListenerSubscription: Subscription
  private readonly ABNORMAL_VALUES_MILLISECONDS_TIME_UPDATE = 600 * 1000
  private readonly isMobile = this.preferenceState.isMobile
  private readonly isUserCNA = this.store.selectSignal(UserState.isUserCNA)
  private messageClosedByUser = false
  private readonly PATIENT_IS_NOT_SELECTED_THROTTLE_SECONDS = 60

  constructor() {
    super()
    this.handleSuccessfullySentToPccBannerHidingAfterNewMqttDataComing()
  }

  @Selector()
  public static isMeasurementSending(state: ReportInterface): boolean {
    return state.isMeasurementSending
  }

  @Selector()
  public static isMeasurementSendingFailed(state: ReportInterface): boolean {
    return state.isMeasurementSendingFailed
  }

  @Selector()
  public static isMessageAboutShortCutShown(state: ReportInterface): boolean {
    return state.isMessageAboutShortCutShown
  }

  @Selector([ReportState, PatientState.reportPatients])
  public static reportAllPatientLength(
    state: ReportInterface,
    reportPatients: Partial<PatientInterface>[]
  ): number {
    if (!reportPatients) return 0
    return reportPatients.length
  }

  @Selector([ReportState])
  public static bulkRequestPatients(state: ReportInterface) {
    return state.requestPatientsVitals
  }

  @Selector([ReportState])
  public static hasUnsentVitalsPopupShown(state: ReportInterface) {
    return state.hasUnsentVitalsPopupShown
  }

  @Selector([ReportState])
  public static isPatientVitalsTakenPopupVisible(state: ReportInterface) {
    return state.isPatientVitalsTakenPopupVisible
  }

  @Selector([ReportState])
  public static isPatientVitalsWereTakenWithoutSelectionPopupVisible(
    state: ReportInterface
  ) {
    return state.isPatientVitalsWereTakenWithoutSelectionPopupVisible.isOpened
  }

  @Selector([ReportState])
  public static isWarningAboutNotSavedOrSentMeasurementsVisible(
    state: ReportInterface
  ) {
    return state.isWarningAboutNotSavedOrSentMeasurementsVisible
  }

  @Selector([ReportState])
  public static successfullySavedMeasurementsOrReportedPatientsIds(
    state: ReportInterface
  ) {
    return state.successfullySavedMeasurementsOrReportedPatientsIds
  }

  @Selector([ReportState, ReportState.report, ReportState.bulkRequestPatients])
  public static requestPatientsWithMissingVitals(
    state: ReportInterface,
    report: Partial<PatientVitalsInterface>[],
    bulkRequestPatients: string[]
  ) {
    return report.filter(
      patient =>
        patient.id &&
        bulkRequestPatients.includes(patient.id) &&
        !patient.hasSomeVitalsTaken
    ).length
  }

  @Selector([ReportState, ReportState.report, ReportState.bulkRequestPatients])
  public static requestPatientsWithAbnormalVitals(
    state: ReportInterface,
    report: Partial<PatientVitalsInterface>[],
    bulkRequestPatients: string[]
  ) {
    return report.filter(
      patient =>
        patient.id &&
        bulkRequestPatients.includes(patient.id) &&
        ((patient.hasSomeVitalsTaken && patient.hasAbnormalVitals) ||
          patient.hasMedications)
    ).length
  }

  @Selector([ReportState, ReportState.report, ReportState.bulkRequestPatients])
  public static requestPatientsWithAllNormalVitals(
    state: ReportInterface,
    report: Partial<PatientVitalsInterface>[],
    bulkRequestPatients: string[]
  ) {
    return report.filter(
      patient =>
        patient.id &&
        bulkRequestPatients.includes(patient.id) &&
        patient.hasSomeVitalsTaken &&
        !patient.hasAbnormalVitals &&
        !patient.hasMedications
    ).length
  }

  @Selector()
  public static reportIsAbnormalMissingFilter(state: ReportInterface): boolean {
    return state.isAbnormalMissingFilter
  }

  @Selector([
    ReportState,
    PatientState.reportPatients,
    ExportState.patientsAllExports,
    ExportState.intervalTrigger,
    DeviceState.reportDevices,
    ShiftPlanerState.currentShiftType,
    DepartmentState.currentDepartments,
    ShiftPlanerState.shifts,
    OrganizationState.emrConfig
  ])
  public static isSentPatientsToEMR(
    state: ReportInterface,
    reportPatients: Partial<PatientInterface>[],
    patientsAllExports: ExportAllInterface[],
    exportsReflowTrigger: string,
    devices: DeviceInterface[],
    currentShift: ShiftType,
    departments: DepartmentDTO[] | null,
    shifts: Shift[],
    emrConfig: EMRConfig
  ): boolean {
    return !!ReportState.hydrate(
      state.medication,
      state.monitorPatientIds,
      state.requestPatientsVitals,
      entitiesFilter(state.textFilter, reportPatients),
      patientsAllExports,
      exportsReflowTrigger,
      devices.filter(d => d.patient && d.patient.id),
      state.forceReadDevicesStatus,
      state.type,
      departments,
      currentShift,
      shifts,
      emrConfig,
      false
    ).filter(p => p.sentToEMR).length
  }

  @Selector([
    ReportState,
    PatientState.reportPatients,
    ExportState.patientsAllExports,
    ExportState.intervalTrigger,
    DeviceState.reportDevices,
    ShiftPlanerState.currentShiftType,
    DepartmentState.currentDepartments,
    ShiftPlanerState.shifts,
    OrganizationState.emrConfig
  ])
  public static report(
    state: ReportInterface,
    reportPatients: Partial<PatientInterface>[],
    patientsAllExports: ExportAllInterface[],
    exportsReflowTrigger: string,
    devices: DeviceInterface[],
    currentShift: ShiftType,
    departments: DepartmentDTO[] | null,
    shifts: Shift[],
    emrConfig: EMRConfig,
    shouldLimitDataWithinTwoHoursInsteadOfCurrentShift?: boolean
  ): Partial<PatientVitalsInterface>[] {
    const data = ReportState.hydrate(
      state.medication,
      state.monitorPatientIds,
      state.requestPatientsVitals,
      entitiesFilter(state.textFilter, reportPatients),
      patientsAllExports,
      exportsReflowTrigger,
      devices.filter(d => d.patient && d.patient.id),
      state.forceReadDevicesStatus,
      state.type,
      departments,
      currentShift,
      shifts,
      emrConfig,
      shouldLimitDataWithinTwoHoursInsteadOfCurrentShift || false
    )
    return !data
      ? []
      : abnormalMissingFilter(data, state.isAbnormalMissingFilter)
  }

  @Selector([
    ReportState,
    PatientState.reportPatients,
    ExportState.patientsAllExports,
    ExportState.intervalTrigger,
    DeviceState.reportDevices,
    ShiftPlanerState.currentShiftType,
    DepartmentState.currentDepartments,
    ShiftPlanerState.shifts,
    OrganizationState.emrConfig
  ])
  public static newReport(
    state: ReportInterface,
    reportPatients: Partial<PatientInterface>[],
    patientsAllExports: ExportAllInterface[],
    exportsReflowTrigger: string,
    devices: DeviceInterface[],
    currentShift: ShiftType,
    departments: DepartmentDTO[] | null,
    shifts: Shift[],
    emrConfig: EMRConfig
  ) {
    return (shouldLimitDataWithinTwoHoursInsteadOfCurrentShift: boolean) => {
      return ReportState.report(
        state,
        reportPatients,
        patientsAllExports,
        exportsReflowTrigger,
        devices,
        currentShift,
        departments,
        shifts,
        emrConfig,
        shouldLimitDataWithinTwoHoursInsteadOfCurrentShift
      )
    }
  }

  @Selector([ReportState, ReportState.report])
  public static patientsWithVitals(
    state: ReportInterface,
    reportPatients: Partial<PatientVitalsInterface>[]
  ) {
    return reportPatients.filter(patient => patient.hasSomeVitalsTaken).length
  }

  @Selector([ReportState, ReportState.report])
  public static allPatientsWithVitalsRecentlySent(
    state: ReportInterface,
    reportPatients: Partial<PatientVitalsInterface>[]
  ) {
    const patientsWithVitals = reportPatients.filter(
      patient => patient.hasSomeVitalsTaken
    )
    return patientsWithVitals.every(patient => patient.sentToEMR)
  }

  @Selector([ReportState, ReportState.report])
  public static allPatientsWithReadyToSentOrNeedsReviewVitals(
    state: ReportInterface,
    reportPatients: Partial<PatientVitalsInterface>[]
  ) {
    return reportPatients.filter(
      patient =>
        patient.patientVitalsStatus === PatientVitalsStatus.NeedsReview ||
        patient.patientVitalsStatus === PatientVitalsStatus.ReadyToSend
    )
  }

  @Selector([ReportState, ReportState.report])
  public static allPatientsWithSentVitals(
    state: ReportInterface,
    reportPatients: Partial<PatientVitalsInterface>[]
  ) {
    return reportPatients.filter(
      patient => patient.patientVitalsStatus === PatientVitalsStatus.Sent
    )
  }

  @Selector()
  public static forceReadProcess(state: ReportInterface) {
    return state.isForceReadProcess
  }

  @Selector([
    ReportState,
    PatientState.reportPatients,
    ExportState.patientsAllExports,
    ExportState.intervalTrigger,
    DeviceState.reportDevices,
    ShiftPlanerState.currentShiftType,
    DepartmentState.currentDepartments,
    ShiftPlanerState.shifts,
    OrganizationState.emrConfig
  ])
  public static reportShift(
    state: ReportInterface,
    reportPatients: Partial<PatientInterface>[],
    patientsAllExports: ExportAllInterface[],
    exportsReflowTrigger: string,
    devices: DeviceInterface[],
    currentShift: ShiftType,
    departments: DepartmentDTO[] | null,
    shifts: Shift[],
    emrConfig: EMRConfig
  ): PatientVitalsInterface[] {
    const data = ReportState.hydrate(
      state.medication,
      state.monitorPatientIds,
      state.requestPatientsVitals,
      entitiesFilter(state.textFilter, reportPatients),
      patientsAllExports,
      exportsReflowTrigger,
      devices.filter(d => d.patient && d.patient.id),
      state.forceReadDevicesStatus,
      state.type,
      departments,
      currentShift,
      shifts,
      emrConfig,
      false
    )
    return !data ? [] : data
  }

  @Selector()
  public static textFilter(state: ReportInterface) {
    return state.textFilter
  }

  @Selector([
    ReportState,
    PatientState.reportPatients,
    ExportState.patientsAllExports,
    ExportState.intervalTrigger,
    DeviceState.reportDevices,
    RootStore.pccSentPatientVitals,
    ShiftPlanerState.currentShiftType,
    DepartmentState.currentDepartments,
    ShiftPlanerState.shifts,
    OrganizationState.emrConfig
  ])
  public static reportAbnormalOrBlankOrWithMedicationAlerts(
    state: ReportInterface,
    reportPatients: Partial<PatientInterface>[],
    patientsAllExports: ExportAllInterface[],
    exportsReflowTrigger: string,
    devices: DeviceInterface[],
    pccSentPatientVitals: string[],
    currentShift: ShiftType,
    departments: DepartmentDTO[] | null,
    shifts: Shift[],
    emrConfig: EMRConfig
  ): PatientVitalsInterface[] {
    const data = ReportState.hydrate(
      state.medication,
      state.monitorPatientIds,
      state.requestPatientsVitals,
      // @ts-ignore
      pccSentPatientVitals.length
        ? reportPatients.filter(
            p => p.enabled && !pccSentPatientVitals.find(id => id === p.id)
          )
        : reportPatients,
      patientsAllExports,
      exportsReflowTrigger,
      devices.filter(d => d.patient && d.patient.id),
      state.forceReadDevicesStatus,
      state.type,
      departments,
      currentShift,
      shifts,
      emrConfig,
      false
    )
    return !data
      ? []
      : data.filter(p => {
          return (
            p.checked &&
            (p.hasAbnormalVitals ||
              p.patientVitalsStatus !== PatientVitalsStatus.Sent ||
              (p.patientVitalsStatus === PatientVitalsStatus.Sent &&
                p.hasAbnormalVitals) ||
              p.hasMedications)
          )
        })
  }

  private static checkingLastEmrTime(data: PatientExportDTO['creationTime']) {
    const differenceInMinutes = moment().diff(moment(data), 'minutes', true)
    return differenceInMinutes < 1
  }

  private static requestPatientsVitalsSetting(
    requestPatientsVitals: string[],
    patients: PatientVitalsInterface[],
    reportsVitalsData: ReportsVitalsInterface
  ) {
    requestPatientsVitals.forEach(id => {
      const patient = patients.find(p => p.id === id)
      if (patient) {
        if (
          patient.vitals &&
          !Object.values(patient.vitals).filter(v => v).length
        ) {
          const dataPatientIdx = reportsVitalsData.patientActions.findIndex(
            d => d.patientId === patient.id
          )
          if (dataPatientIdx !== -1) {
            reportsVitalsData.patientActions[dataPatientIdx].treatmentActions =
              [ReportVitalsFields.MeasureAllVitals]
          } else {
            reportsVitalsData.patientActions.push({
              patientId: patient.id,
              treatmentActions: [ReportVitalsFields.MeasureAllVitals]
            })
          }
        } else {
          const data: {
            patientId: string
            treatmentActions: string[]
          } = {
            patientId: patient.id,
            treatmentActions: []
          }
          if (!patient.vitals?.bodyTemperature) {
            data.treatmentActions = uniq([
              ...data.treatmentActions,
              ReportVitalsFields.MeasureAllVitals
            ])
          }
          if (!patient.vitals?.heartRate) {
            data.treatmentActions = uniq([
              ...data.treatmentActions,
              ReportVitalsFields.MeasureAllVitals
            ])
          }
          if (!patient.vitals?.respirationRate) {
            data.treatmentActions = uniq([
              ...data.treatmentActions,
              ReportVitalsFields.MeasureAllVitals
            ])
          }
          if (
            !patient.vitals?.systolicPressure ||
            !patient.vitals?.diastolicPressure
          ) {
            data.treatmentActions = uniq([
              ...data.treatmentActions,
              ReportVitalsFields.MeasureAllVitals
            ])
          }
          if (!patient.vitals?.spo2) {
            data.treatmentActions = uniq([
              ...data.treatmentActions,
              ReportVitalsFields.MeasureAllVitals
            ])
          }
          const dataPatientIdx = reportsVitalsData.patientActions.findIndex(
            d => d.patientId === patient.id
          )

          if (dataPatientIdx !== -1) {
            reportsVitalsData.patientActions[dataPatientIdx].treatmentActions =
              uniq([
                ...reportsVitalsData.patientActions[dataPatientIdx]
                  .treatmentActions,
                ...data.treatmentActions
              ])
          } else {
            if (!data.treatmentActions.length) return
            reportsVitalsData.patientActions.push(data)
          }
        }
      }
    })
  }

  private static hasAbnormal(
    patient: Partial<PatientVitalsInterface>
  ): boolean {
    const emrRules = cloneDeep(checkEmrRules)
    emrRules.set(
      !patient.patientAlertRules
        ? patient.defaultAlertRules!
        : patient.patientAlertRules
    )
    let hasAbnormal = false

    if (
      Object.values(patient.measurement?.latestPerVital || {}).filter(v => v)
        .length
    ) {
      if (
        patient.measurement?.latestPerVital?.systolicPressure &&
        patient.measurement?.latestPerVital?.systolicPressure.value &&
        !(
          (patient.measurement?.latestPerVital?.systolicPressure
            .value as number) <=
            emrRules.get(ObservationFields.SystolicPressure).max &&
          (patient.measurement?.latestPerVital?.systolicPressure
            .value as number) >=
            emrRules.get(ObservationFields.SystolicPressure).min
        )
      ) {
        hasAbnormal = true
      }

      if (
        patient.measurement?.latestPerVital?.diastolicPressure &&
        patient.measurement?.latestPerVital?.diastolicPressure.value &&
        !(
          (patient.measurement?.latestPerVital?.diastolicPressure
            .value as number) <=
            emrRules.get(ObservationFields.DiastolicPressure).max &&
          (patient.measurement?.latestPerVital?.diastolicPressure
            .value as number) >=
            emrRules.get(ObservationFields.DiastolicPressure).min
        )
      ) {
        hasAbnormal = true
      }

      if (
        patient.measurement?.latestPerVital?.heart_rate &&
        patient.measurement?.latestPerVital?.heart_rate.value &&
        !(
          (patient.measurement?.latestPerVital?.heart_rate.value as number) <=
            emrRules.get(ObservationFields.HeartRate).max &&
          (patient.measurement?.latestPerVital?.heart_rate.value as number) >=
            emrRules.get(ObservationFields.HeartRate).min
        )
      ) {
        hasAbnormal = true
      }

      if (
        patient.measurement?.latestPerVital?.spo2 &&
        patient.measurement?.latestPerVital?.spo2.value &&
        !(
          (patient.measurement?.latestPerVital?.spo2.value as number) <=
            emrRules.get(ObservationFields.SpO2).max &&
          (patient.measurement?.latestPerVital?.spo2.value as number) >=
            emrRules.get(ObservationFields.SpO2).min
        )
      ) {
        hasAbnormal = true
      }

      if (
        patient.measurement?.latestPerVital?.body_temperature &&
        patient.measurement?.latestPerVital?.body_temperature.value &&
        !(
          round(
            patient.measurement?.latestPerVital?.body_temperature
              .value as number,
            1
          ) <= emrRules.get(ObservationFields.BodyTemperature).max &&
          round(
            patient.measurement?.latestPerVital?.body_temperature
              .value as number,
            1
          ) >= emrRules.get(ObservationFields.BodyTemperature).min
        )
      ) {
        hasAbnormal = true
      }

      if (
        patient.measurement?.latestPerVital?.respiration_rate &&
        patient.measurement?.latestPerVital?.respiration_rate.value &&
        !(
          (patient.measurement?.latestPerVital?.respiration_rate
            .value as number) <=
            emrRules.get(ObservationFields.RespirationRate).max &&
          (patient.measurement?.latestPerVital?.respiration_rate
            .value as number) >=
            emrRules.get(ObservationFields.RespirationRate).min
        )
      ) {
        hasAbnormal = true
      }
    }

    return hasAbnormal
  }

  private static setAllToEmrSetting(
    patient: Partial<PatientVitalsInterface>,
    exports: BffObservationExport[],
    methodsData: {
      [K in MethodsDataSelectedKeys]: ObservationExportMethodsData
    }
  ) {
    const emrRules = cloneDeep(checkEmrRules)
    emrRules.set(
      !patient.patientAlertRules
        ? patient.defaultAlertRules!
        : patient.patientAlertRules
    )
    const patientExport: BffObservationExport | undefined = exports.find(
      (e: BffObservationExport) => e.observedPatient === patient.id
    )
    let data: {
      observedPatient?: string
      systolicPressure?: number
      diastolicPressure?: number
      heartRate?: number
      respirationRate?: number
      spo2?: number
      bodyTemperature?: number
      bloodGlucose?: number
      bloodPressureMethod?: string
      heartRateMethod?: string
      spo2Method?: string
      bodyTemperatureMethod?: string
    } = {}
    if (patient.hasAbnormalVitals) {
      return null
    }
    if (
      Object.values(patient.measurement?.latestPerVital || {}).filter(v => v)
        .length
    ) {
      if (
        patient.measurement?.latestPerVital?.systolicPressure &&
        patient.measurement?.latestPerVital?.systolicPressure?.value &&
        (patient.measurement?.latestPerVital?.systolicPressure
          ?.value as number) <=
          emrRules.get(ObservationFields.SystolicPressure).max &&
        (patient.measurement?.latestPerVital?.systolicPressure
          .value as number) >=
          emrRules.get(ObservationFields.SystolicPressure).min
      ) {
        data.systolicPressure = patient.measurement?.latestPerVital
          ?.systolicPressure?.value as number
      }

      if (
        patient.measurement?.latestPerVital?.diastolicPressure &&
        patient.measurement?.latestPerVital?.diastolicPressure?.value &&
        (patient.measurement?.latestPerVital?.diastolicPressure
          .value as number) <=
          emrRules.get(ObservationFields.DiastolicPressure).max &&
        (patient.measurement?.latestPerVital?.diastolicPressure
          .value as number) >=
          emrRules.get(ObservationFields.DiastolicPressure).min
      ) {
        data.diastolicPressure = patient.measurement?.latestPerVital
          ?.diastolicPressure?.value as number
      }

      if (
        patient.measurement?.latestPerVital?.heart_rate &&
        patient.measurement?.latestPerVital?.heart_rate?.value &&
        (patient.measurement?.latestPerVital?.heart_rate.value as number) <=
          emrRules.get(ObservationFields.HeartRate).max &&
        (patient.measurement?.latestPerVital?.heart_rate.value as number) >=
          emrRules.get(ObservationFields.HeartRate).min
      ) {
        data.heartRate = patient.measurement?.latestPerVital?.heart_rate
          ?.value as number
      }

      if (
        patient.measurement?.latestPerVital?.spo2 &&
        patient.measurement?.latestPerVital?.spo2?.value &&
        (patient.measurement?.latestPerVital?.spo2.value as number) <=
          emrRules.get(ObservationFields.SpO2).max &&
        (patient.measurement?.latestPerVital?.spo2.value as number) >=
          emrRules.get(ObservationFields.SpO2).min
      ) {
        data.spo2 = patient.measurement?.latestPerVital?.spo2?.value as number
      }

      if (
        patient.measurement?.latestPerVital?.body_temperature &&
        patient.measurement?.latestPerVital?.body_temperature?.value &&
        round(
          patient.measurement?.latestPerVital?.body_temperature
            ?.value as number,
          1
        ) <= emrRules.get(ObservationFields.BodyTemperature).max &&
        round(
          patient.measurement?.latestPerVital?.body_temperature
            ?.value as number,
          1
        ) >= emrRules.get(ObservationFields.BodyTemperature).min
      ) {
        data.bodyTemperature = patient.measurement?.latestPerVital
          ?.body_temperature?.value as number
      }

      if (
        patient.measurement?.latestPerVital?.respiration_rate &&
        patient.measurement?.latestPerVital?.respiration_rate?.value &&
        (patient.measurement?.latestPerVital?.respiration_rate
          .value as number) <=
          emrRules.get(ObservationFields.RespirationRate).max &&
        (patient.measurement?.latestPerVital?.respiration_rate
          .value as number) >=
          emrRules.get(ObservationFields.RespirationRate).min
      ) {
        data.respirationRate = patient.measurement?.latestPerVital
          ?.respiration_rate?.value as number
      }
    }

    data.bloodPressureMethod = !patientExport?.blood_pressure_method
      ? methodsData.bloodPressure.defaultMethod?.emrCode
      : patientExport?.blood_pressure_method
    data.heartRateMethod = !patientExport?.heart_rate_method
      ? methodsData.heartRate.defaultMethod?.emrCode
      : patientExport?.heart_rate_method
    data.spo2Method = !patientExport?.spo2_method
      ? methodsData.spo2.defaultMethod?.emrCode
      : patientExport?.spo2_method
    data.bodyTemperatureMethod = !patientExport?.body_temperature_method
      ? methodsData.bodyTemperature.defaultMethod?.emrCode
      : patientExport?.body_temperature_method

    return { observedPatient: patient.id, ...data }
  }

  private static setTreatmentPlanSetting(treatmentPlan: TreatmentPlanDTO[]): {
    needToCheckLength: number
    tmpObj: CheckShiftSentEmrInformationInterface
  } {
    let needToCheckLength = 0
    const tmpObj: CheckShiftSentEmrInformationInterface = {
      status: 'NEED_EXPORT_TO_EMR'
    }

    treatmentPlan.forEach(t => {
      if (t.BT) {
        needToCheckLength += 1
        tmpObj.bt = false
        tmpObj.btStatus = 'NEED_SEND_TO_EMR'
      }
      if (t.BG) {
        needToCheckLength += 1
        tmpObj.bg = false
        tmpObj.bgStatus = 'NEED_SEND_TO_EMR'
      }
      if (t.SPO2) {
        needToCheckLength += 1
        tmpObj.spo2 = false
        tmpObj.spo2Status = 'NEED_SEND_TO_EMR'
      }
      if (t.BP) {
        needToCheckLength += 1
        tmpObj.bp = false
        tmpObj.bpStatus = 'NEED_SEND_TO_EMR'
      }
      if (t.HR) {
        needToCheckLength += 1
        tmpObj.hr = false
        tmpObj.hrStatus = 'NEED_SEND_TO_EMR'
      }
      if (t.RR) {
        needToCheckLength += 1
        tmpObj.rr = false
        tmpObj.rrStatus = 'NEED_SEND_TO_EMR'
      }
    })

    return {
      needToCheckLength,
      tmpObj
    }
  }

  private static hydrate(
    medication: ReportMedicationInterface[],
    monitorPatientIds: string[],
    requestPatientsVitals: string[],
    patients: PatientInterface[],
    patientsAllExports: ExportAllInterface[],
    exportsReflowTrigger: string,
    devices: DeviceInterface[],
    forceReadDevicesStatus: NewReadSocketModel[],
    type: ReportType,
    departments: DepartmentDTO[] | null,
    currentShift: ShiftType,
    shifts: Shift[],
    emrConfig: EMRConfig,
    shouldLimitDataWithinTwoHoursInsteadOfCurrentShift: boolean
  ): PatientVitalsInterface[] {
    if (!departments || !departments.length) {
      return []
    }
    return patients.map(p => {
      const currentMedication = medication.filter(
        m => m.patientId === p.id && m.alerts.length
      )
      const currentDevices = devices
        .filter(d => d.model === DeviceModel.BiobeatWatch)
        .find(d => d.patient.id === p.id)
      const patientAllExport = patientsAllExports.find(
        e => e.patientId === p.id
      )

      const patientForceReadDevicesStatus = forceReadDevicesStatus.filter(
        fr => fr.deviceId === currentDevices?.id
      )
      const checkShiftSentEmrInformation = p.treatmentPlan
        ? ReportState.toReportShiftCheckVitalsToTreatmentPlan(
            p.treatmentPlan.filter(
              t => !t.endTime || new Date(t.endTime) > new Date()
            ),
            !patientAllExport
              ? { patientId: p.id, data: [] }
              : patientAllExport,
            shifts
          )
        : null

      const patientMeasurement =
        shouldLimitDataWithinTwoHoursInsteadOfCurrentShift
          ? p.measurement
          : ReportState.checkCurrentShiftReportTime(type, p.measurement, shifts)

      const measurement = ReportState.checkCurrentShiftReportTime(
        type,
        patientMeasurement,
        shifts
      )
      const { vitals } = ReportState.toPatientVitals(
        p,
        measurement,
        type,
        shifts.find(shift => shift.type === currentShift) || shifts[0]
      )

      p.requiredVitals = requiredVitals(
        { ...p, measurement: patientMeasurement },
        currentShift || ShiftType.FirstShift
      )
      p.hasSomeVitalsTaken = ReportState.hasSomeVitalsTaken(patientMeasurement)
      p.hasSomeNonManualVitalsTaken =
        ReportState.hasSomeNonManualVitalsTaken(patientMeasurement)

      p.treatmentPlan =
        p.treatmentPlan && p.treatmentPlan.length
          ? [
              ...p?.treatmentPlan.filter(
                tp =>
                  !tp?.endTime ||
                  (tp.endTime && tp.endTime > new Date().toISOString())
              )
            ]
          : []

      const vitalsTakenGroup: PatientVitalsTakenGroup =
        ReportState.setPatientVitalsGroup(
          {
            ...p,
            measurement: patientMeasurement
          },
          currentShift || ShiftType.FirstShift
        )

      let lastEmrUpdateVitalsTime
      let lastEmrUpdatePainTime
      let lastEmrAnyStatusUpdateVitalsTime

      const exportsWithPainLevel = patientAllExport?.data.filter(
        e =>
          e.pain_level !== null &&
          e.pain_level !== undefined &&
          e.export_status === 'completed'
      )
      const exportsWithoutPainLevel = patientAllExport?.data.filter(
        e =>
          ((e.diastolic_pressure !== null &&
            e.systolic_pressure !== null &&
            e.diastolic_pressure !== undefined &&
            e.systolic_pressure !== undefined) ||
            (e.heart_rate !== null && e.heart_rate !== undefined) ||
            (e.spo2 !== null && e.spo2 !== undefined) ||
            (e.body_temperature !== null && e.body_temperature !== undefined) ||
            (e.respiration_rate !== null &&
              e.respiration_rate !== undefined)) &&
          e.export_status === 'completed'
      )
      const anyExportsWithoutPainLevel = patientAllExport?.data.filter(
        e =>
          (e.diastolic_pressure !== null && e.systolic_pressure !== null) ||
          e.heart_rate !== null ||
          e.spo2 !== null ||
          e.body_temperature !== null ||
          e.respiration_rate !== null
      )
      if (patientMeasurement) {
        if (exportsWithPainLevel && exportsWithPainLevel.length) {
          lastEmrUpdatePainTime = exportsWithPainLevel.sort((a, b) =>
            b.creationTime.localeCompare(a.creationTime)
          )[0].creationTime
        }
        if (exportsWithoutPainLevel && exportsWithoutPainLevel.length) {
          lastEmrUpdateVitalsTime = exportsWithoutPainLevel.sort((a, b) =>
            b.creationTime.localeCompare(a.creationTime)
          )[0].creationTime
        }
        if (anyExportsWithoutPainLevel && anyExportsWithoutPainLevel.length) {
          lastEmrAnyStatusUpdateVitalsTime = anyExportsWithoutPainLevel.sort(
            (a, b) => b.creationTime.localeCompare(a.creationTime)
          )[0].creationTime
        }
      }

      const hasReportingCooldown = emrConfig.hasReportingCooldown

      return {
        measurement: patientMeasurement,
        hasSomeVitalsTaken: p.hasSomeVitalsTaken,
        hasSomeNonManualVitalsTaken: p.hasSomeNonManualVitalsTaken,
        hasAbnormalVitals: ReportState.hasAbnormal({
          ...p,
          measurement: patientMeasurement,
          insights: undefined
        }),
        firstName: p.firstName,
        lastName: p.lastName,
        patientVitalsStatus: ReportState.toPatientVitalsStatus(
          { ...p, measurement: patientMeasurement, insights: undefined },
          exportsWithoutPainLevel
        ),
        sentToEMR:
          lastEmrAnyStatusUpdateVitalsTime && hasReportingCooldown
            ? ReportState.checkingLastEmrTime(lastEmrAnyStatusUpdateVitalsTime)
            : false,
        painSentToEMR:
          lastEmrUpdatePainTime && hasReportingCooldown
            ? ReportState.checkingLastEmrTime(lastEmrUpdatePainTime)
            : false,
        checked: !!requestPatientsVitals.filter(id => id === p.id).length,
        cuffSize: p.cuffSize,
        vitalsTakenGroup,
        id: p.id,
        enabled: p.enabled,
        emrid: p.emrid || null,
        room: p.room,
        departmentName: p.department.name,
        departmentId: p.department.id,
        forceReadLoading: ReportState.forceReadLoadingSetting(
          patientForceReadDevicesStatus.length
            ? patientForceReadDevicesStatus[0]
            : null
        ),
        lastEMRUpdateTime: p.lastEMRUpdateTime,
        lastMeasurementTimeWithoutPainLevel:
          p.measurement?.lastMeasurementTimeWithoutPainLevel,
        lastEmrUpdatePainTime,
        lastEmrUpdateVitalsTime,
        name: `${p.lastName}, ${p.firstName}`,
        avatarUrl: p.avatar,
        rpm: p.rpm,
        rpm_status: p.rpm_status,
        isPatientRPMActive: p.isPatientRPMActive,
        statusDetailInformation:
          currentDevices?.statusDetailInformation || 'Active',
        monitored: monitorPatientIds.includes(p.id),
        patientAlertRules: p.patientAlertRules,
        hasPersonalSystolicBloodPressureThreshold:
          p.patientAlertRules?.lastModifiedBy === 'system',
        requiredVitals: p.requiredVitals,
        wasSentToEMR:
          patientAllExport && patientAllExport.data.length
            ? ReportState.toPatientWasSentToEmr(
                patientAllExport.data[patientAllExport.data.length - 1]
                  .creationTime,
                measurement?.lastMeasurementTimeWithoutPainLevel as string
              )
            : false,
        defaultAlertRules: p.defaultAlertRules,
        ...ReportState.toPatientVitalsMissing(
          checkShiftSentEmrInformation,
          vitals,
          type
        ),
        ...ReportState.toPatientVitalsEmrStatus(checkShiftSentEmrInformation),
        ...ReportState.toPatientVitalsInsight(p.insights),
        ...ReportState.toPatientVitalsMeasurementTime(
          measurement,
          type,
          shifts.find(shift => shift.type === currentShift)!
        ),
        medication: currentMedication.length ? currentMedication[0].alerts : [],
        hasMedications: currentMedication.length
          ? !!currentMedication[0].alerts.length
          : false,
        checkShiftSentEmrInformation: p.treatmentPlan
          ? ReportState.toReportShiftCheckVitalsToTreatmentPlan(
              p.treatmentPlan.filter(
                t => !t.endTime || new Date(t.endTime) > new Date()
              ),
              // @ts-ignore
              !patientAllExport
                ? { patientId: p.id, data: [] }
                : patientAllExport,
              shifts
            )
          : null,
        alertRules: p.alertRules
      }
    })
  }

  private static forceReadLoadingSetting(
    forceReadDeviceStatus: NewReadSocketModel | null
  ) {
    return !(
      !forceReadDeviceStatus ||
      forceReadDeviceStatus.forceReadStatus === forceReadStatus.Monitor ||
      forceReadDeviceStatus.forceReadStatus === forceReadStatus.Removed ||
      forceReadDeviceStatus.forceReadStatus === forceReadStatus.Disconnected ||
      forceReadDeviceStatus.errorCode
    )
  }

  //TODO: We should maintain current shift object with all parsed data like from time / till time in the store and make all calculations in selectors
  private static checkCurrentShiftReportTime(
    type: string,
    report: PatientObservationDTO | null,
    shifts: Shift[]
  ) {
    if (type === ReportType.Time) {
      return report
    } else if (!report) {
      return null
    } else {
      const shift = ShiftPlanerState.getShiftByTime(shifts, new Date())
      return isFromCurrentShift(report.lastMeasurementTime as string, shift)
        ? {
            ...report,
            latestPerVital: report.latestPerVital
              ? Object.fromEntries(
                  Object.entries(report.latestPerVital).filter(([key, value]) =>
                    isFromCurrentShift(value.timestamp as string, shift)
                  )
                )
              : report.latestPerVital
          }
        : null
    }
  }

  private static toPatientVitalsEmrStatus(
    checkShiftSentEmrInformation: CheckShiftSentEmrInformationInterface | null
  ) {
    const statusMapping = {
      [SentEmrInformation.Completed]: {
        emrStatus: VitalStatusIndicator.Completed,
        isVitalComplete: true,
        vitalStatusIcon: VitalStatusIcon.Completed
      },
      [SentEmrInformation.NotRequired]: {
        emrStatus: null,
        isVitalComplete: false,
        vitalStatusIcon: VitalStatusIcon.NotRequired
      },
      [SentEmrInformation.NeedExportToEmr]: {
        emrStatus: VitalStatusIndicator.NeedExportToEmr,
        isVitalComplete: false,
        vitalStatusIcon: VitalStatusIcon.NeedExportToEmr
      },
      [SentEmrInformation.CriticalReportTime]: {
        emrStatus: VitalStatusIndicator.NeedExportToEmr,
        isVitalComplete: false,
        vitalStatusIcon: VitalStatusIcon.NeedExportToEmr
      },
      [SentEmrInformation.FailedEmr]: {
        emrStatus: VitalStatusIndicator.FailedExportToEmr,
        isVitalComplete: false,
        vitalStatusIcon: VitalStatusIcon.NotRequired
      },
      [SentEmrInformation.PartialExport]: {
        emrStatus: VitalStatusIndicator.PartialExportEmr,
        isVitalComplete: true,
        vitalStatusIcon: VitalStatusIcon.PartialExportEmr
      }
    }

    const defaultStatus = {
      emrStatus: null,
      isVitalComplete: false,
      vitalStatusIcon: VitalStatusIcon.NotRequired
    }

    const status = checkShiftSentEmrInformation?.status

    const { emrStatus, isVitalComplete, vitalStatusIcon } = status
      ? statusMapping[status] || defaultStatus
      : defaultStatus

    return {
      emrStatus,
      isVitalComplete,
      vitalStatusIcon,
      checkShiftSentEmrInformation
    }
  }

  private static toPatientVitalsInsight(insight: InsightDTO[] | null): {
    insights: PatientVItalInsightsObservationFields
  } {
    const fields: {
      key: keyof PatientVItalInsightsObservationFields
      subject: AlertSubject
    }[] = [
      { key: 'systolicPressure', subject: AlertSubject.SystolicPressure },
      { key: 'diastolicPressure', subject: AlertSubject.DiastolicPressure },
      { key: 'heartRate', subject: AlertSubject.HeartRate },
      { key: 'respirationRate', subject: AlertSubject.RespirationRate },
      { key: 'spo2', subject: AlertSubject.SPO2 },
      { key: 'bodyTemperature', subject: AlertSubject.BodyTemperature }
    ]

    const result = fields.reduce((acc, field) => {
      acc[field.key] = insight
        ? extractSpecificInsightBySubject(insight, field.subject)
        : null
      return acc
    }, {} as PatientVItalInsightsObservationFields)

    return { insights: result }
  }

  private static toPatientVitalsMeasurementTime(
    measurement: PatientObservationDTO | null,
    reportType: string,
    shift: Shift
  ) {
    const isOldMeasurementTime = checkFreshVitalCurrentShift(
      shift,
      measurement?.lastMeasurementTimeWithoutPainLevel as string
    )
    const isActualLastMeasurementTime =
      (measurement?.lastMeasurementTimeWithoutPainLevel &&
        (!isOldMeasurementTime || reportType === ReportType.Time)) as boolean
    return {
      isOldMeasurementTime,
      lastMeasurementTime:
        measurement?.lastMeasurementTimeWithoutPainLevel || null,
      isActualLastMeasurementTime
    }
  }

  private static toPatientWasSentToEmr(
    lastExportTime: string,
    lastMeasurementTime: string
  ) {
    if (!lastMeasurementTime) return false
    return lastMeasurementTime <= lastExportTime
  }

  private static toPatientVitalsMissing(
    checkShiftSentEmrInformation: CheckShiftSentEmrInformationInterface | null,
    v: PatientVItalObservationFields,
    reportType: ReportType
  ) {
    if (Object.values(v).filter(v => v).length) {
      Object.keys(v).forEach(key => {
        if (
          isNull(v[key as VitalsFields]) &&
          !isNull(checkShiftSentEmrInformation) &&
          checkShiftSentEmrInformation.status !== SentEmrInformation.Completed
        ) {
          v[key as VitalsFields] = {
            isMissing: CheckMissingVital(
              v,
              checkShiftSentEmrInformation,
              key as VitalsFields,
              reportType
            )
          }
        }
      })
    }
    return { vitals: { ...v } }
  }

  private static toPatientVitals(
    patient: PatientInterface,
    measurement: PatientObservationDTO | null,
    reportType: string,
    shift: Shift
  ): { vitals: PatientVItalObservationFields } {
    const vitals: {
      key: keyof PatientVItalObservationFields
      subject: ObservationFields
    }[] = [
      {
        key: VitalsFields.SystolicPressure,
        subject: ObservationFields.SystolicPressure
      },
      {
        key: VitalsFields.DiastolicPressure,
        subject: ObservationFields.DiastolicPressure
      },
      { key: VitalsFields.HeartRate, subject: ObservationFields.HeartRate },
      {
        key: VitalsFields.RespirationRate,
        subject: ObservationFields.RespirationRate
      },
      { key: VitalsFields.SpO2, subject: ObservationFields.SpO2 },
      {
        key: VitalsFields.BodyTemperature,
        subject: ObservationFields.BodyTemperature
      },
      {
        key: VitalsFields.BloodGlucose,
        subject: ObservationFields.BloodGlucose
      }
    ]

    const result = vitals.reduce((acc, vital) => {
      const v =
        measurement?.latestPerVital?.[
          vital.subject as keyof PatientObservationDTO['latestPerVital']
        ]
      acc[vital.key] = v
        ? ReportState.setPatientVitalSetting(
            v,
            patient,
            reportType,
            vital.subject,
            shift
          )
        : null
      return acc
    }, {} as PatientVItalObservationFields)

    return { vitals: result }
  }

  private static toReportShiftCheckVitalsToTreatmentPlan(
    treatmentPlans: TreatmentPlanDTO[],
    { data }: ExportAllInterface,
    shifts: Shift[]
  ) {
    return checkVitalsToTreatmentPlan(
      combineTreatmentPlans(treatmentPlans),
      data,
      shifts
    )
  }

  private static setPatientVitalSetting(
    vital: ObservationLatest,
    patient: PatientInterface,
    reportType: string,
    vitalType: string,
    shift: Shift
  ) {
    const isOldVital = checkFreshVitalCurrentShift(
      shift,
      vital?.timestamp as string
    )
    const isCriticalVital = checkCriticalRules.checkVitalToCriticalRule(
      vital?.value,
      vitalType as ObservationFields
    )
    const isAbnormalVital = checkVitalToEmrRules(
      vital?.value,
      vitalType,
      patient
    )
    const isErrorDueToTypoBecameCritical =
      checkCriticalRules.checkVitalToCriticalRuleDueToTypo(
        vital?.value,
        vitalType as ObservationFields
      )
    return {
      value: vital.value,
      isManual: checkIsManual(vital?.isManual as boolean),
      timestamp: vital?.timestamp,
      isAbnormalVital,
      isActualValue:
        vital.value && (!isOldVital || reportType === ReportType.Time),
      isCriticalVital,
      isOldVital,
      isMissing: !vital.value || isOldVital,
      isErrorDueToTypoBecameCritical,
      checked: false
    }
  }

  private static hasSomeVitalsTaken(
    patientMeasurements: PatientObservationDTO | null
  ): boolean {
    if (!patientMeasurements || !patientMeasurements.latestPerVital) {
      return false
    }
    if (
      Object.entries(patientMeasurements.latestPerVital).filter(
        ([key, v]) => key !== 'painLevel' && !!v
      ).length
    ) {
      return true
    }
    return false
  }

  private static hasSomeNonManualVitalsTaken(
    patientMeasurements: PatientObservationDTO | null
  ): boolean {
    if (!patientMeasurements || !patientMeasurements.latestPerVital) {
      return false
    }
    if (
      Object.entries(patientMeasurements.latestPerVital).filter(
        ([key, v]) => !!v && !v.isManual && key !== 'painLevel'
      ).length
    ) {
      return true
    }
    return false
  }

  private static toPatientVitalsStatus(
    patient: Partial<PatientVitalsInterface>,
    exports: BffObservationExport[] | undefined
  ): PatientVitalsStatus {
    if (!ReportState.hasSomeVitalsTaken(patient.measurement ?? null)) {
      return PatientVitalsStatus.DataRequired
    }
    if (exports === undefined || exports.length === 0) {
      if (ReportState.hasAbnormal(patient)) {
        return PatientVitalsStatus.NeedsReview
      }
      return PatientVitalsStatus.ReadyToSend
    }

    if (
      ReportState.exportWasPerformedWithAllVitalsAfterMeasured(
        patient.measurement!,
        ReportState.combineExportedVitalsWithTimestamp(exports)
      )
    ) {
      return PatientVitalsStatus.Sent
    }
    if (ReportState.hasAbnormal(patient)) {
      return PatientVitalsStatus.NeedsReview
    }
    return PatientVitalsStatus.ReadyToSend
  }

  private static exportWasPerformedWithAllVitalsAfterMeasured(
    measurements: PatientObservationDTO,
    patientExport: PatientExportedVitalsWithTimestamp
  ): boolean {
    const systolicCondition = !measurements.latestPerVital?.systolicPressure
      ? true
      : !!patientExport.systolic_pressure &&
        measurements.latestPerVital?.systolicPressure?.value ===
          patientExport.systolic_pressure.value &&
        measurements.latestPerVital?.systolicPressure?.timestamp <
          patientExport.systolic_pressure.creationTime
    const diastolicCondition = !measurements.latestPerVital?.diastolicPressure
      ? true
      : !!patientExport.diastolic_pressure &&
        measurements.latestPerVital?.diastolicPressure?.value ===
          patientExport.diastolic_pressure.value &&
        measurements.latestPerVital?.diastolicPressure?.timestamp <
          patientExport.diastolic_pressure.creationTime
    const heartRateCondition = !measurements.latestPerVital?.heart_rate
      ? true
      : !!patientExport.heart_rate &&
        measurements.latestPerVital?.heart_rate?.value ===
          patientExport.heart_rate.value &&
        measurements.latestPerVital?.heart_rate.timestamp <
          patientExport.heart_rate.creationTime
    const spo2Condition = !measurements.latestPerVital?.spo2
      ? true
      : !!patientExport.spo2 &&
        measurements.latestPerVital?.spo2?.value === patientExport.spo2.value &&
        measurements.latestPerVital?.spo2.timestamp <
          patientExport.spo2.creationTime
    const bodyTemperatureCondition = !measurements.latestPerVital
      ?.body_temperature
      ? true
      : !!patientExport.body_temperature &&
        measurements.latestPerVital?.body_temperature?.value.toFixed(3) ===
          patientExport.body_temperature.value?.toFixed(3) &&
        measurements.latestPerVital?.body_temperature.timestamp <
          patientExport.body_temperature.creationTime
    const respirationRateCondition = !measurements.latestPerVital
      ?.respiration_rate
      ? true
      : !!patientExport.respiration_rate &&
        measurements.latestPerVital?.respiration_rate?.value ===
          patientExport.respiration_rate.value &&
        measurements.latestPerVital?.respiration_rate.timestamp <
          patientExport.respiration_rate.creationTime
    return (
      systolicCondition &&
      diastolicCondition &&
      heartRateCondition &&
      spo2Condition &&
      bodyTemperatureCondition &&
      respirationRateCondition
    )
  }

  private static combineExportedVitalsWithTimestamp(
    patientExports: BffObservationExport[]
  ): PatientExportedVitalsWithTimestamp {
    return orderBy(patientExports, 'creationTime', 'asc').reduce(
      (prev, next) => {
        if (next.systolic_pressure) {
          prev.systolic_pressure = {
            ...(prev.systolic_pressure || {}),
            value: next.systolic_pressure,
            creationTime: next.creationTime
          }
        }
        if (next.diastolic_pressure) {
          prev.diastolic_pressure = {
            ...(prev.diastolic_pressure || {}),
            value: next.diastolic_pressure,
            creationTime: next.creationTime
          }
        }
        if (next.heart_rate) {
          prev.heart_rate = {
            ...(prev.heart_rate || {}),
            value: next.heart_rate,
            creationTime: next.creationTime
          }
        }
        if (next.spo2) {
          prev.spo2 = {
            ...(prev.spo2 || {}),
            value: next.spo2,
            creationTime: next.creationTime
          }
        }
        if (next.body_temperature) {
          prev.body_temperature = {
            ...(prev.body_temperature || {}),
            value: next.body_temperature,
            creationTime: next.creationTime
          }
        }
        if (next.respiration_rate) {
          prev.respiration_rate = {
            ...(prev.respiration_rate || {}),
            value: next.respiration_rate,
            creationTime: next.creationTime
          }
        }
        return prev
      },
      {
        systolic_pressure: null,
        diastolic_pressure: null,
        spo2: null,
        heart_rate: null,
        body_temperature: null,
        respiration_rate: null
      } as PatientExportedVitalsWithTimestamp
    )
  }

  private static setPatientVitalsGroup(
    patient: PatientInterface,
    currentShift: ShiftType
  ): PatientVitalsTakenGroup {
    if (!patient?.treatmentPlan || !patient.treatmentPlan.length) {
      if (patient.hasSomeVitalsTaken) {
        return PatientVitalsTakenGroup.Recorded
      }
      return PatientVitalsTakenGroup.NotRequired
    }

    const currentShiftTreatmentPlans = patient.treatmentPlan.filter(
      tp =>
        tp.frequency?.includes(currentShift) &&
        isTakingTodayVitalsRequired(tp) &&
        tp.additionalInformation &&
        tp.additionalInformation.length
    )

    if (!currentShiftTreatmentPlans.length) {
      if (patient.hasSomeVitalsTaken) {
        return PatientVitalsTakenGroup.Recorded
      }
      return PatientVitalsTakenGroup.NotRequired
    }

    if (isAnyVitalsMissing(patient, currentShift)) {
      return PatientVitalsTakenGroup.VitalsRound
    }
    return PatientVitalsTakenGroup.Recorded
  }

  public override ngxsOnInit() {
    this.storeEvents.devicesModified$
      .pipe(
        tap(devicesAction => {
          this.getMonitoredPatients(devicesAction.action.entities)
          if (this.reportStateSubscription)
            this.reportStateSubscription.unsubscribe()
          this.reportStateSubscription =
            this.backendService.updatePatientVitalsManualWSOnMessageObs$.subscribe(
              (res: any) => {
                clearTimeout(this.timeout)
                if (this.getState().type !== ReportType.CurrentShift) return
                if (
                  (this.checkVitalsInData(cloneDeep(res)) &&
                    !this.lastPatientReportTime[res.patientId]) ||
                  (this.checkVitalsInData(cloneDeep(res)) &&
                    this.lastPatientReportTime[res.patientId] !== res.timestamp)
                ) {
                  this.updatePatientsIds = uniq([
                    ...this.updatePatientsIds,
                    res.patientId
                  ])
                  this.lastPatientReportTime[res.patientId] = res.timestamp
                }
              }
            )
        })
      )
      .subscribe()

    this.medicationSubscription =
      this.measurementState.getMedication$.subscribe(medication =>
        this.patchState({ medication })
      )

    this.storeEvents.patientsModified$
      .pipe(
        tap(action => {
          this.getReportsData(action.action.patients)
        })
      )
      .subscribe()

    this.storeEvents.navigatedOutFromPatient$
      .pipe(
        tap(() => {
          this.getReportsData()
        })
      )
      .subscribe()

    if (!this.isMobile) {
      merge(
        this.storeEvents.patientsModified$,
        this.storeEvents.shiftChanged$,
        this.storeEvents.homePageNavigated$.pipe(
          skipWhile(() => this.patientState.getState().ids.length === 0)
        ),
        interval(this.ABNORMAL_VALUES_MILLISECONDS_TIME_UPDATE)
      )
        .pipe(
          switchMap(() => {
            const isAdmin = this.ngxPermissionsService.getPermission(
              Permission.Admin
            )

            if (this.authState.isAuthenticated() && !isAdmin) {
              return this.getPreviousShiftsAbnormalData()
            }
            return EMPTY
          }),
          catchError(() => {
            this.ntfService.error(
              `Error in getting latest abnormal values. Please, try reload page.`
            )
            return EMPTY
          })
        )
        .subscribe()

      this.storeEvents.patientsResetPartially$
        .pipe(
          tap(() =>
            this.measurementState.setMeasurementWithAbnormalInPreviousShiftsLoading(
              true
            )
          )
        )
        .subscribe()
    }

    if (this.isMobile) {
      merge(
        this.storeEvents.canLoadTabletSpotlightPatients$.pipe(
          switchMap(() => this.storeEvents.patientsModified$.pipe(take(1)))
        ),
        this.storeEvents.getTabletSpotlightPatients$.pipe(
          tap(() => {
            this.measurementState.setMeasurementWithAbnormalInPreviousShiftsLoading(
              true
            )
            this.measurementState.setMeasurementWithAbnormalInPreviousShiftsVisible(
              true
            )
          })
        )
      )
        .pipe(
          switchMap(() =>
            this.getPreviousShiftsAbnormalData().pipe(
              takeUntil(this.storeEvents.closeTabletSpotlightPatients$)
            )
          )
        )
        .subscribe()
    }

    this.storeEvents.shiftChanged$
      .pipe(
        tap(() => {
          if (this.authState.isAuthenticated()) {
            this.patchState({
              currentShift: ShiftPlanerState.getShiftByTime(
                this.shiftPlannerState.getState().shifts,
                new Date()
              ).title
            })
            this.measurementState.refreshMeasurement()
            this.exportState.refreshExport()
            this.patchState({ medication: [] })
            this.measurementState.setLoading(true)
            this.getReportsData()
            this.setIsPatientVitalsWereTakenWithoutSelectionPopupVisible({
              isOpened: false,
              closedTime: null
            })
          }
        })
      )
      .subscribe()

    this.storeEvents.departmentsChange$
      .pipe(
        tap(() => {
          this.measurementState.setLoading(true)
          this.setTextFilter('')
          this.measurementState.setMeasurementWithAbnormalInPreviousShiftsLoading(
            true
          )
          if (!this.isMobile) {
            this.setSuccessfullySavedMeasurementsOrReportedPatientsIds(null)
          }
        })
      )
      .subscribe()

    this.subscribeCompletedPccSentData()

    this.storeEvents.loggedIn$
      .pipe(
        tap(() => {
          this.setLoggedInDate()
          this.setCheckShiftChangeInterval()
          this.handleLocalDeviceMeasurementsPollingWhenPatientIsNotSelectedForVitalsTakenSession()
        })
      )
      .subscribe()

    this.storeEvents.logout$
      .pipe(
        tap(() => {
          this.updatePatientsIds = []
          clearTimeout(this.timeout)
          if (this.readonlylocalDeviceMeasurementsMessageSubscription) {
            this.readonlylocalDeviceMeasurementsMessageSubscription.unsubscribe()
          }
          this.reset()
          this.lastPatientReportTime = {}
          if (this.checkTimeSubscription) {
            this.checkTimeSubscription.unsubscribe()
          }
          if (this.reportStateSubscription)
            this.reportStateSubscription.unsubscribe()
          if (this.currentShiftListenerSubscription)
            this.currentShiftListenerSubscription.unsubscribe()
          if (this.medicationSubscription) {
            this.medicationSubscription.unsubscribe()
          }
          if (this.checkTimeSubscription) {
            this.checkTimeSubscription.unsubscribe()
          }
        })
      )
      .subscribe()
  }

  setCheckShiftChangeInterval() {
    if (this.checkTimeSubscription) this.checkTimeSubscription.unsubscribe()
    this.checkTimeSubscription = timer(5000, 30000).subscribe(() =>
      this.checkCurrentShiftTime()
    )
  }

  closeMessage() {
    this.messageClosedByUser = true
    this.setIsMessageAboutShortCutShown(false)
    this.setCheckShiftChangeInterval()
  }

  resetMessages() {
    this.messageClosedByUser = false
    this.showed10MinutesMessage = false
    this.showed30MinutesMessage = false
    this.setIsMessageAboutShortCutShown(false)
  }

  @DataAction()
  setNewRead() {
    let patients = Object.values(this.patientState.entities)
    const departments = this.departmentState.getState().currentDepartments

    if (departments && departments.length) {
      patients = patients.filter(
        patient =>
          patient.department &&
          departments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const currentDevices = Object.values(this.deviceState.entities).filter(
      d =>
        d.model === DeviceModel.BiobeatWatch &&
        d.forceReadEnabled &&
        d.statusInformation !== DeviceCriticalStatus.NoConnection &&
        d.patient &&
        patients.find(p => p.id === d.patient.id)
    )
    if (!currentDevices) return
    this.ntfService.warning(
      'The system is retrieving fresh data from devices. This process may take a few minutes'
    )
    this.forceReadSocketInitialization(currentDevices.map(d => d.id))
  }

  @DataAction()
  setReportsAllToEmrData(
    @Payload('patients') patients: Partial<PatientVitalsInterface>[],
    @Payload('exports') exports: BffObservationExport[],
    @Payload('methodsData')
    methodsData: {
      [K in MethodsDataSelectedKeys]: ObservationExportMethodsData
    }
  ) {
    this.pccState.setLoading(true)
    const observations = patients
      .filter(
        p =>
          !p.sentToEMR && p.emrid && p.hasSomeVitalsTaken && !p.hasMedications
      )
      .map(p => ReportState.setAllToEmrSetting(p, exports, methodsData))
      .filter(d => !!d)
    if (!observations.length) {
      setTimeout(() => {
        this.pccState.setLoading(false)
        this.ntfService.error(
          `There are no good residents for reporting to the EMR`
        )
      }, 0)
      return
    }

    const observationsVitalsLength = observations
      .map(getVitalsFromReportingObject)
      .map(vitals => Object.keys(vitals).length)
      .reduce((prev, next) => {
        return prev + next
      }, 0)

    this.logrocketPFFacade.logrocketTrack(LogrocketTrackKeys.BulkReportAll, {
      description: observationsVitalsLength
    })
    this.pccState
      .updatePccEmrMeasurementBulk(
        {
          // @ts-ignore
          observations
        },
        patients.length
      )
      .pipe(
        finalize(() =>
          this.setSuccessfullySavedMeasurementsOrReportedPatientsIds(null)
        )
      )
  }

  @DataAction()
  setIsAbnormalMissingFilter(
    @Payload('isAbnormalMissingFilter') isAbnormalMissingFilter: boolean
  ) {
    this.patchState({
      isAbnormalMissingFilter
    })
  }

  @DataAction()
  setHasUnsentVitalsPopupShown(
    @Payload('hasUnsentVitalsPopupShown') hasUnsentVitalsPopupShown: boolean
  ) {
    this.ctx.patchState({
      hasUnsentVitalsPopupShown
    })
  }

  @DataAction()
  setSuccessfullySavedMeasurementsOrReportedPatientsIds(
    @Payload('patientId') patientId: string | null
  ) {
    if (patientId === null) {
      this.ctx.patchState({
        successfullySavedMeasurementsOrReportedPatientsIds: []
      })
      return
    }
    const successfullySavedMeasurementsOrReportedPatientsIds =
      this.getState().successfullySavedMeasurementsOrReportedPatientsIds
    this.ctx.patchState({
      successfullySavedMeasurementsOrReportedPatientsIds: [
        ...new Set([
          ...successfullySavedMeasurementsOrReportedPatientsIds,
          patientId
        ])
      ]
    })
  }

  @DataAction()
  setIsModalWithBulkSendingPatientsVisible(
    @Payload('isModalWithBulkSendingPatientsVisible')
    isModalWithBulkSendingPatientsVisible: boolean
  ) {
    this.ctx.patchState({
      isModalWithBulkSendingPatientsVisible
    })
  }

  @DataAction()
  setIsPatientVitalsTakenPopupVisible(
    @Payload('isPatientVitalsTakenPopupVisible')
    isPatientVitalsTakenPopupVisible: boolean
  ) {
    this.ctx.patchState({
      isPatientVitalsTakenPopupVisible
    })
  }

  @DataAction()
  setIsPatientVitalsWereTakenWithoutSelectionPopupVisible(
    @Payload('isPatientVitalsWereTakenWithoutSelectionPopupVisible')
    isPatientVitalsWereTakenWithoutSelectionPopupVisible: ReportInterface['isPatientVitalsWereTakenWithoutSelectionPopupVisible']
  ) {
    this.ctx.patchState({
      isPatientVitalsWereTakenWithoutSelectionPopupVisible
    })
  }

  @DataAction()
  setIsWarningAboutNotSavedOrSentMeasurementsVisible(
    @Payload('isWarningAboutNotSavedOrSentMeasurementsVisible')
    isWarningAboutNotSavedOrSentMeasurementsVisible: boolean
  ) {
    this.ctx.patchState({
      isWarningAboutNotSavedOrSentMeasurementsVisible
    })
  }

  @DataAction()
  setIsMessageAboutShortCutShown(
    @Payload('isMessageAboutShortCutShown')
    isMessageAboutShortCutShown: boolean
  ) {
    this.ctx.patchState({
      isMessageAboutShortCutShown
    })
  }

  @DataAction()
  setIsMeasurementSendingFailed(
    @Payload('isMeasurementSendingFailed')
    isMeasurementSendingFailed: boolean
  ) {
    this.ctx.patchState({
      isMeasurementSendingFailed
    })
  }

  public setCurrentShiftListener(): Observable<number> {
    this.patchState({
      currentShift: ShiftPlanerState.getShiftByTime(
        this.shiftPlannerState.getState().shifts,
        new Date()
      ).type
    })
    return timer(10000, 10000).pipe(
      switchMap(n => {
        if (
          this.getState().currentShift !==
          ShiftPlanerState.getShiftByTime(
            this.shiftPlannerState.getState().shifts,
            new Date()
          ).type
        ) {
          this.patchState({
            currentShift: ShiftPlanerState.getShiftByTime(
              this.shiftPlannerState.getState().shifts,
              new Date()
            ).type
          })
          this.measurementState.refreshMeasurement()
          this.exportState.refreshExport()
          this.patchState({ medication: [] })
        }
        return of(n)
      })
    )
  }

  @DataAction()
  setTextFilter(@Payload('text') text: string) {
    this.patchState({ textFilter: text })
  }

  @DataAction()
  exportPDFSetting(
    @Payload('patients') patients: Partial<PatientVitalsInterface>[]
  ) {
    const departments = !!this.departmentState.getState().currentDepartments
      ? this.departmentState.getState().currentDepartments
      : [Object.values(this.departmentState.entities)[0]]
    const temporaryDepartment = [
      ...orderBy(Object.values(departments!), 'name', 'asc')
    ][0]

    const rows = patients
      .filter(p => p.enabled)
      .filter(p => p.departmentId === temporaryDepartment.id)
      .map(p => ({
        name: p.name,
        room: !p.room ? '' : p.room,
        bp: p.measurement?.latestPerVital?.systolicPressure?.value
          ? p.measurement?.latestPerVital?.systolicPressure?.value +
            '/' +
            p.measurement?.latestPerVital?.diastolicPressure?.value
          : '',
        hr: String(p.measurement?.latestPerVital?.heart_rate?.value || ''),
        rr: String(
          p.measurement?.latestPerVital?.respiration_rate?.value || ''
        ),
        spo: String(p.measurement?.latestPerVital?.spo2?.value || ''),
        temp: String(
          p.measurement?.latestPerVital?.body_temperature?.value
            ? bodyTemperatureSetting.toTransformCelsiusToFahrenheit(
                p.measurement?.latestPerVital?.body_temperature?.value as number
              )
            : ''
        ),
        lastMeasurements: !p.measurement?.lastMeasurementTimeWithoutPainLevel
          ? ''
          : moment(p.measurement?.lastMeasurementTimeWithoutPainLevel).format(
              'MM/DD/yy HH:mm'
            )
      }))
    if (!rows) {
      return
    }
    return this.backendService.reports({
      facilityName: temporaryDepartment.departmentEmrId,
      departmentName: temporaryDepartment.name,
      rows
    })
  }

  @DataAction()
  setReportsVitalsData(
    @Payload('patients') patients: PatientVitalsInterface[],
    @Payload('idx') ids?: string[]
  ) {
    this.reportsVitalsData = {
      patientActions: []
    }
    const requestPatientsVitals: string[] = ids
      ? cloneDeep(ids)
      : this.ctx.getState().requestPatientsVitals
    if (requestPatientsVitals.length) {
      ReportState.requestPatientsVitalsSetting(
        requestPatientsVitals,
        patients,
        this.reportsVitalsData
      )
    }
    return this.backendService
      .setManualVitals(this.reportsVitalsData)
      .pipe(tap(() => this.ntfService.success('Task(s) were created')))
  }

  // @DataAction()
  setManualCNAReport(
    @Payload('data') data: ManualVitalsInterface,
    @Payload('type') type: ReportType,
    @Payload('isLastEmitted') isLastEmitted: boolean = true
  ) {
    const { title, ...dataWithoutTitle } = data
    this.patchState({ isMeasurementSending: true })
    this.setIsMeasurementSendingFailed(false)
    this.measurementState.addPatientMeasurementSendingId(data.observedPatient)

    const isSimulationMode = this.store.selectSnapshot(
      PreferenceState.isSimulationMode
    )

    const measurement$ = isSimulationMode
      ? this.backendService.setCNAManualVitalsDummy(dataWithoutTitle)
      : this.backendService.setCNAManualVitals(dataWithoutTitle)

    return measurement$.pipe(
          tap((measurementResponse: { stringPayload: string }) => {
            const measurement = convertMeasurementFromStringPayload({
              stringPayload: measurementResponse.stringPayload,
              patientId: data.observedPatient,
              deviceId: data.deviceId
            })
            this.measurementState.measurementsWSOnMessageCallback(measurement)

            if (isLastEmitted) {
              this.patchState({ type })
              this.patchState({ isMeasurementSending: false })
              if (this.isUserCNA()) {
                this.setSuccessfullySavedMeasurementsOrReportedPatientsIds(
                  data.observedPatient
                )
                this.userState.updateConsecutiveDaysUsage()
              }
              this.measurementState.removePatientMeasurementSendingId(
                data.observedPatient
              )
            }

            this.isUserRN$.pipe(take(1)).subscribe(isUserRn => {
              if (
                (this.authState.getState().CNAAccessJwt ||
                  ((isUserRn ||
                    this.permissionsService.canReportVitalsToEMR()) &&
                    this.isMobile)) &&
                data?.title !== WarningTitle.Warning
              ) {
                return
              }

              if (!isLastEmitted) {
                return
              }

              const reportPatients = this.store.selectSnapshot(
                ReportState.report
              )
              this.measurementState.removePatientMeasurementSendingId(
                data.observedPatient
              )
              let patient = reportPatients.find(
                p => p.id === data.observedPatient
              )
              if (patient?.checkShiftSentEmrInformation?.status) {
                patient = {
                  ...patient,
                  checkShiftSentEmrInformation: {
                    ...patient.checkShiftSentEmrInformation,
                    status: 'NOT_REQUIRED'
                  }
                }
              }
              if (
                patient?.vitalsTakenGroup ===
                  PatientVitalsTakenGroup.Recorded &&
                patient.requiredVitals &&
                Object.keys(patient.requiredVitals).length &&
                this.isMobile
              ) {
                this.setSuccessfullySentCurrentShiftPatientAllMeasurements(
                  patient
                )
              } else {
                if (patient) {
                  this.ntfService.success(
                    `${patient.lastName}, ${
                      (patient?.firstName || '')[0]
                    }. vitals recorded`
                  )
                } else {
                  this.ntfService.success(`Update Manual Vitals completed`)
                }
              }
            })
          }),
          catchError(() => {
            this.setIsMeasurementSendingFailed(true)
            this.patchState({ isMeasurementSending: false })
            this.measurementState.removePatientMeasurementSendingId(
              data.observedPatient
            )
            this.ntfService.error(`Update Manual Vitals Failed`)
            return EMPTY
          })
        )
  }

  @DataAction()
  getReportsData(patientsData?: BffPatient[]): void {
    const isAdmin = this.ngxPermissionsService.getPermission(Permission.Admin)
    if (isAdmin) {
      this.performanceState.setTotalFinishPerformance()
      this.performanceState.getTotalPerformance()
      return
    }
    let patients = patientsData || Object.values(this.patientState.entities)
    const departments = this.departmentState.getState().currentDepartments

    if (departments && departments.length) {
      patients = patients.filter(
        patient =>
          patient.department &&
          departments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const rooms = this.patientState.getState().rooms
    if (rooms.length) {
      patients = patients.filter(patient => rooms.includes(patient.room))
    }
    const activePatientIds = patients.map(p => p.id)
    if (!activePatientIds.length) {
      this.measurementState.setLoading(false)
      this.performanceState.setTotalFinishPerformance()
      this.performanceState.getTotalPerformance()
      return
    }
    this.performanceState.setMeasurementsStartPerformance()
    if (this.getState().type === ReportType.CurrentShift) {
      this.getCurrentShiftLatestData(activePatientIds)
    } else {
      const currentShift = this.shiftPlannerState.getState().currentShift
      const previousShift = this.shiftPlannerState.getPreviousShift(
        currentShift?.type!
      )
      const nowTime = moment()
      let startTime = moment()
        .set({ hours: previousShift.start })
        .startOf('hour')
      let endTime = moment().set({ hours: previousShift.end }).startOf('hour')
      if (nowTime.hours() >= 0 && nowTime.hours() < previousShift!.end) {
        startTime.subtract(1, 'days')
        endTime.subtract(1, 'days')
      } else if (previousShift.end < previousShift.start) {
        startTime.subtract(1, 'days')
      }
      this.measurementState.getMeasurementSummary(
        activePatientIds,
        startTime.toISOString(),
        endTime.toISOString(),
        ReportMode.Latest
      )
    }
  }

  @DataAction()
  getMonitoredPatients(devices: DeviceDTO[]) {
    let patients = Object.values(this.patientState.entities)
    const departments = this.departmentState.getState().currentDepartments

    if (departments && departments.length) {
      patients = patients.filter(
        patient =>
          patient.department &&
          departments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const monitorPatientIds = devices
      .filter(d => d.patient)
      .map(d => d.patient.id)

    this.ctx.patchState({
      monitorPatientIds: [
        ...new Set([...monitorPatientIds, ...this.getState().monitorPatientIds])
      ]
    })
  }

  @DataAction()
  setDevicesForceRead(@Payload('type') type = 'default') {
    let patients = Object.values(this.patientState.entities)
    const departments = this.departmentState.getState().currentDepartments

    if (departments && departments.length) {
      patients = patients.filter(
        patient =>
          patient.department &&
          departments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const currentDevices = Object.values(this.deviceState.entities).filter(
      d =>
        d.model === DeviceModel.BiobeatWatch &&
        d.forceReadEnabled &&
        d.patient &&
        patients.find(p => p.id === d.patient.id)
    )
    return this.backendService
      .setDevicesForceRead(currentDevices.map(d => d.id))
      .pipe(
        tap(
          (
            res: {
              deviceId: string
              forceReadStatus: string
            }[]
          ) => {
            if (type !== 'default') return
            res.forEach(r => {
              const device = currentDevices.find(d => d.id === r.deviceId)
              if (r.forceReadStatus === 'DONE') {
                this.ntfService.success(`${device?.serialNumber} New Read Done`)
              } else {
                this.ntfService.error(`${device?.serialNumber} New Read Failed`)
              }
            })
          }
        )
      )
  }

  @DataAction()
  updatePatientVitalsManualWSOnMessageSetting(
    @Payload('patientIds') ids: string[]
  ) {
    if (this.getState().type === ReportType.CurrentShift) {
      this.measurementState.getMeasurementSummary(
        ids,
        moment(new Date()).subtract(8, 'hours').toISOString(),
        moment(new Date()).toISOString(),
        ModeType.Latest
      )
    } else if (this.getState().type === ReportType.Time) {
      this.measurementState.getMeasurementSummary(
        ids,
        moment(new Date()).subtract(1, 'hours').toISOString(),
        moment(new Date()).toISOString(),
        ModeType.Latest
      )
      // this.getCurrentShiftLatestData(ids)
    }
  }

  @DataAction()
  setSuccessfullySentCurrentShiftPatientAllMeasurements(
    patient: Partial<PatientVitalsInterface> | null,
    shouldHideAutomatically: boolean = true
  ): Observable<Partial<PatientVitalsInterface> | null> {
    this.successfullySentCurrentShiftPatientAllMeasurements$.next(patient)
    if (!shouldHideAutomatically) {
      return of(patient)
    }
    timer(3000)
      .pipe(take(1))
      .subscribe(() =>
        this.successfullySentCurrentShiftPatientAllMeasurements$.next(null)
      )
    return of(patient)
  }

  @DataAction()
  showCompletedPccSentData(
    @Payload('patient') patient: Partial<PatientVitalsInterface>
  ) {
    const isVitalsRecorded =
      patient?.vitalsTakenGroup === PatientVitalsTakenGroup.Recorded &&
      patient.requiredVitals &&
      Object.keys(patient.requiredVitals).length

    if (
      this.isMobile &&
      (this.permissionsService.isRnMode() ||
        this.permissionsService.canReportVitalsToEMR())
    ) {
      this.setSuccessfullySentCurrentShiftPatientAllMeasurements(patient, false)
      return
    }
    if (isVitalsRecorded) {
      if (this.isMobile) {
        this.setSuccessfullySentCurrentShiftPatientAllMeasurements(patient)
      } else {
        this.ntfService.success(
          `${patient.lastName}, ${
            (patient.firstName || '')[0]
          }. vitals saved and reported`
        )
      }
    } else {
      this.store
        .select(ReportState.isMeasurementSending)
        .pipe(
          filter(isSending => !isSending),
          take(1),
          switchMap(() =>
            this.store
              .selectOnce(ReportState.report)
              .pipe(map(patients => patients.find(p => p.id === patient.id)))
          )
        )
        .subscribe(pat => {
          if (
            !this.isMobile ||
            !(
              pat?.vitalsTakenGroup === PatientVitalsTakenGroup.Recorded &&
              pat.requiredVitals &&
              Object.keys(pat.requiredVitals).length
            )
          ) {
            this.ntfService.success(
              `${pat?.lastName}, ${
                (pat?.firstName || '')[0]
              }. vitals saved and reported`
            )
          } else {
            this.setSuccessfullySentCurrentShiftPatientAllMeasurements(pat!)
          }
        })
    }
  }

  @DataAction()
  getTabletSpotlightPatients() {
    return
  }

  @DataAction()
  closeTabletSpotlightPatients() {
    return
  }

  @DataAction()
  setLoggedInDate(): void {
    this.ctx.patchState({ nowDate: new Date().toISOString() })
  }

  checkCurrentShiftTime() {
    const date = new Date()
    const currentShift = ShiftPlanerState.getShiftByTime(
      this.shiftPlannerState.getState().shifts,
      date
    )
    const time = moment()
      .set('hours', currentShift.end)
      .set('minutes', 0)
      .set('seconds', 0)
    const minutesToNextShift = time.diff(moment(), 'minutes')

    this.logrocketPFFacade.logrocketLog(
      'Now',
      new Date().toLocaleString(),
      'End Of Shift',
      time.toDate().toLocaleString(),
      'Minutes To Next Shift',
      minutesToNextShift
    )
    if (minutesToNextShift > 0 && minutesToNextShift <= 30) {
      this.checkTime(time.toDate())
    } else if (minutesToNextShift > 30 || minutesToNextShift < 0) {
      this.resetMessages()
    }
  }

  protected setPaginationSetting(): Observable<any> {
    return EMPTY
  }

  protected loadEntitiesFromBackend(
    ids: string[] | undefined
  ): Observable<void> {
    return EMPTY
  }

  private checkTime(date: Date) {
    if (!date) return
    const currentTime = new Date().getTime()
    const endOfShiftTime = date.getTime()
    const timeUntilShiftEnd = endOfShiftTime - currentTime
    if (timeUntilShiftEnd > 30 * 60 * 1000) {
      this.resetMessages()
    } else if (
      timeUntilShiftEnd <= 30 * 60 * 1000 &&
      timeUntilShiftEnd > 10 * 60 * 1000 &&
      !this.showed30MinutesMessage
    ) {
      this.setIsMessageAboutShortCutShown(true)
      this.showed30MinutesMessage = true
    } else if (
      (timeUntilShiftEnd < 30 * 60 * 1000 &&
        timeUntilShiftEnd <= 10 * 60 * 1000 &&
        this.showed30MinutesMessage &&
        !this.showed10MinutesMessage &&
        this.messageClosedByUser) ||
      (timeUntilShiftEnd < 30 * 60 * 1000 &&
        timeUntilShiftEnd <= 10 * 60 * 1000 &&
        !this.showed30MinutesMessage &&
        !this.showed10MinutesMessage &&
        !this.messageClosedByUser)
    ) {
      this.setIsMessageAboutShortCutShown(true)
      this.showed30MinutesMessage = true
      this.showed10MinutesMessage = true
      return
    }
  }

  private forceReadSocketInitialization(deviceIds: string[]) {
    this.webSocketService.setUrl(
      `${environment.forceReadWsUrl}/v2/device/force-read?authorization=${this.authState.snapshot.accessJwt?.token}`
    )
    this.webSocketService.send({
      deviceIds,
      timeout: 180
    })
    this.patchState({ isForceReadProcess: true, forceReadDevicesStatus: [] })
    let finishedDevicesLength = 0
    this.subscriptionNewReadSocket$ = this.webSocketService
      .connect()
      .subscribe((data: NewReadSocketModel) => {
        let forceReadDevicesStatus = cloneDeep(
          this.getState().forceReadDevicesStatus
        )
        const idx = forceReadDevicesStatus.findIndex(
          item => item.deviceId === data.deviceId
        )
        if (idx !== -1) {
          forceReadDevicesStatus[idx] = data
        } else {
          forceReadDevicesStatus = [...forceReadDevicesStatus, data]
        }
        this.patchState({ forceReadDevicesStatus })
        if (
          data.forceReadStatus === forceReadStatus.Monitor ||
          data.forceReadStatus === forceReadStatus.Removed ||
          data.forceReadStatus === forceReadStatus.Disconnected ||
          data.errorCode
        ) {
          finishedDevicesLength += 1
          if (finishedDevicesLength === deviceIds.length) {
            this.webSocketService.close()
            this.patchState({
              isForceReadProcess: false,
              forceReadDevicesStatus: []
            })
            this.subscriptionNewReadSocket$.unsubscribe()
          }
        }
      })
  }

  private checkVitalsInData(res: any) {
    if (res.context) {
      delete res.context
    }
    if (res.deviceId) {
      delete res.deviceId
    }
    if (res.monitoringStatus) {
      delete res.monitoringStatus
    }
    if (res.patientId) {
      delete res.patientId
    }
    if (res.sessionId) {
      delete res.sessionId
    }
    if (res.timestamp) {
      delete res.timestamp
    }
    return !!Object.values(res).length
  }

  private getCurrentShiftLatestData(activePatientIds: string[]) {
    const shiftStartDate = this.getShiftCurrentDate()
    this.measurementState.getMeasurementSummary(
      activePatientIds,
      moment(shiftStartDate).toISOString(),
      moment(new Date()).add(20, 'minutes').toISOString(),
      ReportMode.Latest
    )
  }

  private getShiftCurrentDate() {
    const currentShift = ShiftPlanerState.getShiftByTime(
      this.shiftPlannerState.getState().shifts,
      new Date()
    )
    const now = new Date()
    const day =
      currentShift.start > currentShift.end &&
      now.getHours() >= 0 &&
      now.getHours() < currentShift.end
        ? now.getDate() - 1
        : now.getDate()
    let shiftStartDate = new Date(
      now.getFullYear(),
      now.getMonth(),
      day,
      currentShift.start
    )
    if (this.permissionsService.shouldLoadSummaryForJustTwoHoursBack()) {
      shiftStartDate = moment().subtract(2, 'hours').toDate()
    }
    return shiftStartDate.toISOString()
  }

  private getPreviousShiftsAbnormalData(pats?: BffPatient[]) {
    let patients = pats || Object.values(this.patientState.entities)
    const departments = this.departmentState.getState().currentDepartments

    if (departments && departments.length) {
      patients = patients.filter(
        patient =>
          patient.department &&
          departments
            .map(department => department.id)
            .includes(patient.department.id)
      )
    }
    const rooms = this.patientState.getState().rooms
    if (rooms.length) {
      patients = patients.filter(patient => rooms.includes(patient.room))
    }

    const activePatientIds = patients.map(p => p.id)

    if (activePatientIds.length === 0) {
      this.measurementState.setMeasurementWithAbnormalInPreviousShiftsLoading(
        false
      )
      return EMPTY
    }

    return this.measurementState.getAbnormalMeasurementSummary(
      activePatientIds,
      moment().subtract(72, 'hour').toISOString(),
      moment().toISOString(),
      ReportMode.Recent_Abnormal
    )
  }

  private subscribeCompletedPccSentData(): void {
    this.pccState.successfullySentToPccPatientObs$
      .pipe(
        switchMap(patientData => {
          if (!patientData) {
            return of(null)
          }
          return this.store.selectOnce(ReportState.report).pipe(
            map(patients => ({
              patient: patients.find(p => p.id === patientData.patient.id),
              isPainLevelExport: patientData.isPainLevelExport
            }))
          )
        }),
        tap(patientData => {
          if (!patientData || !patientData.patient) {
            return
          }
          this.showCompletedPccSentData(patientData.patient)
          if (!patientData.isPainLevelExport && patientData.patient.id) {
            this.setSuccessfullySavedMeasurementsOrReportedPatientsIds(
              patientData.patient.id
            )
            this.userState.updateConsecutiveDaysUsage()
          }
        })
      )
      .subscribe()
  }

  private handleSuccessfullySentToPccBannerHidingAfterNewMqttDataComing(): void {
    this.measurementState.emitMqttReceivedDataObs$
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (this.getState().isModalWithBulkSendingPatientsVisible) {
          return
        }
        this.pccState.setAmountOfSentPatientsViaBulkReportingProcess(0)
      })
  }

  private handleLocalDeviceMeasurementsPollingWhenPatientIsNotSelectedForVitalsTakenSession(): void {
    if (this.readonlylocalDeviceMeasurementsMessageSubscription) {
      this.readonlylocalDeviceMeasurementsMessageSubscription.unsubscribe()
    }

    const delay = this.PATIENT_IS_NOT_SELECTED_THROTTLE_SECONDS

    this.readonlylocalDeviceMeasurementsMessageSubscription =
      this.deviceState.readonlylocalDeviceMeasurementsMessageObs$
        .pipe(
          distinct(d => d.id + d.status.last_update_time),
          filter(d => {
            const isPatientVitalsWereTakenWithoutSelectionPopupVisible =
              this.getState()
                .isPatientVitalsWereTakenWithoutSelectionPopupVisible

            const lastMeasurementTimeThatWasSaved =
              this.deviceState.getState().lastMeasurementTimeThatWasSaved

            if (this.getState().isPatientVitalsTakenPopupVisible) {
              return false
            }

            if (
              !(
                (d.status.bp_systolic && d.status.bp_diastolic) ||
                d.status.heart_rate ||
                d.status.spo2 ||
                d.status.temp ||
                d.status.respiration_rate
              )
            ) {
              return false
            }

            if (d.status.last_update_time <= this.getState().nowDate) {
              return false
            }

            if (
              isPatientVitalsWereTakenWithoutSelectionPopupVisible.closedTime &&
              moment().diff(
                moment(
                  isPatientVitalsWereTakenWithoutSelectionPopupVisible.closedTime
                ),
                'seconds'
              ) <= delay
            ) {
              return false
            }

            if (
              lastMeasurementTimeThatWasSaved &&
              lastMeasurementTimeThatWasSaved.lastDeviceMeasurementTimeThatWasSaved &&
              d.status.last_update_time <=
                moment(
                  lastMeasurementTimeThatWasSaved.lastDeviceMeasurementTimeThatWasSaved
                )
                  .add(delay, 'seconds')
                  .toISOString()
            ) {
              return false
            }

            return true
          }),
          takeUntilDestroyed()
        )
        .subscribe(d => {
          if (this.getState().isPatientVitalsTakenPopupVisible) {
            return
          }
          if (!this.authState.isAuthenticated()) {
            return
          }
          this.setIsPatientVitalsWereTakenWithoutSelectionPopupVisible({
            isOpened: true,
            closedTime: null
          })
        })
  }
}
